import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, AlertCircle, AlertTriangle, Trash2, X, MessageCircle, Settings as SettingsIcon, Bot } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import AdminWhatsAppTab from '../components/AdminWhatsAppTab';

import { API_BASE, apiFetch } from '../lib/api';

interface SettingItem {
  value: string;
  description: string;
}

const defaultSettings: Record<string, SettingItem> = {
  site_name: { value: '香港辣谷', description: '網站名稱' },
  business_phone: { value: '85251164453', description: 'WhatsApp 業務電話' },
  admin_phone: { value: '85251164453', description: '管理員通知電話' },
  bank_account: { value: 'DBS A/C - 016-000227829', description: '銀行帳號' },
  fps_id: { value: 'FPS - 108810334', description: 'FPS 轉數快 ID' },
  company_name: { value: 'DELICIOUS EXPRESS LTD', description: '公司名稱' },
  free_shipping_threshold: { value: '200', description: '免運門檻（HKD，例如 200）' },
  shipping_above_threshold_label: { value: '免運費', description: '達到免運門檻時顯示的運費說明' },
  shipping_below_threshold_label: { value: '順豐到付（滿 HK$200 包郵）', description: '未達免運門檻時顯示的運費說明' },
  hong_kong_spicy_valley_gift_code: { value: 'HKSPICY10', description: '「香港辣谷」專屬禮品碼（永久9折）' },
};

type DangerTarget = 'orders' | 'customers' | 'referrals';

interface DangerActionDef {
  key: DangerTarget;
  label: string;
  description: string;
  confirmPhrase: string;
  endpoint: string;
}

const DANGER_ACTIONS: DangerActionDef[] = [
  {
    key: 'orders',
    label: '清除所有訂單',
    description: '永久刪除 order_records 內所有訂單記錄，無法復原。',
    confirmPhrase: 'DELETE_ORDERS',
    endpoint: '/api/admin/data/orders',
  },
  {
    key: 'customers',
    label: '清除所有客戶資料',
    description: '永久刪除 member_profiles 內所有客戶資料，無法復原。',
    confirmPhrase: 'DELETE_CUSTOMERS',
    endpoint: '/api/admin/data/customers',
  },
  {
    key: 'referrals',
    label: '清除所有推薦資料',
    description: '永久刪除 referrers 與相關推薦設定，無法復原。',
    confirmPhrase: 'DELETE_REFERRALS',
    endpoint: '/api/admin/data/referrals',
  },
];

import { API_BASE, apiFetch } from '../lib/api';

// ── AI 助手控制台（2026-10-16 AIBOT 整合；2026-10-17 擴充 AI 掌櫃管理卡）──
// 連線卡（URL/Tenant/Key 狀態＋測試）＋總掣（ai_assistant_enabled）＋規則（冷卻/時段）
// ＋通知卡（目標顯示＋測試通知）＋分類卡（開關＋關鍵字表）＋最近互動＋👍👎 反饋
const AiAssistantTab: React.FC = () => {
  const [cfg, setCfg] = useState({ enabled: false, url: '', tenantId: '', apiKeySet: false, cooldownSec: 60, activeHours: '', notifyTarget: '', autotagEnabled: true });
  const [recent, setRecent] = useState<{ phone: string; preview: string; status: string; created_at: number }[]>([]);
  const [feedback, setFeedback] = useState<{ id: number; phone: string; feedback: string; comment: string | null; created_at: number }[]>([]);
  const [keywords, setKeywords] = useState<Record<string, string[]>>({});
  const [notifyTesting, setNotifyTesting] = useState(false);
  const [notifyResult, setNotifyResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/public/admin/ai-assistant/config');
      const json = await res.json().catch(() => ({}));
      const d = json.data ?? json;
      setCfg({
        enabled: !!d.enabled, url: d.url || '', tenantId: d.tenantId || '',
        apiKeySet: !!d.apiKeySet, cooldownSec: Number(d.cooldownSec || 60), activeHours: d.activeHours || '',
        notifyTarget: d.notifyTarget || '', autotagEnabled: d.autotagEnabled !== false,
      });
      setKeywords(d.autotagKeywords || {});
      const [recentRes, fbRes] = await Promise.all([
        apiFetch('/api/public/admin/ai-assistant/recent').then((r) => r.json()).catch(() => null),
        apiFetch('/api/public/admin/ai-assistant/feedback').then((r) => r.json()).catch(() => null),
      ]);
      setRecent(((recentRes?.data ?? recentRes)?.recent || []) as any);
      setFeedback(((fbRes?.data ?? fbRes)?.feedback || []) as any);
    } catch { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/public/admin/ai-assistant/config', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: cfg.enabled, url: cfg.url, tenantId: cfg.tenantId, cooldownSec: cfg.cooldownSec, activeHours: cfg.activeHours, autotagEnabled: cfg.autotagEnabled }),
      });
      await load();
    } finally { setSaving(false); }
  };

  const testNotify = async () => {
    setNotifyTesting(true);
    setNotifyResult(null);
    try {
      const res = await apiFetch('/api/public/admin/ai-assistant/test-notify', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const d = json.data ?? json;
      setNotifyResult({ ok: !!d.ok, text: d.ok ? `已發送→${d.target}——查收群組即知通知鏈正常` : `失敗：${d.error || '未知'}` });
    } catch (e) {
      setNotifyResult({ ok: false, text: String(e) });
    } finally { setNotifyTesting(false); }
  };

  const testConn = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await apiFetch('/api/public/admin/ai-assistant/test', { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      const d = json.data ?? json;
      setTestResult({ ok: !!d.ok, text: d.ok ? `連線成功，AI 回覆：${String(d.answer || '').slice(0, 100)}` : `失敗：${d.error || '未知'}` });
    } catch (e) {
      setTestResult({ ok: false, text: String(e) });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800">載入中…</div>;

  return (
    <div className="space-y-4">
      {/* 連線卡 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">🔗 AIBOT 連線</h3>
        <div className="mt-3 grid gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">/ask 端點 URL</label>
            <input value={cfg.url} onChange={(e) => setCfg({ ...cfg, url: e.target.value })} placeholder="https://wtsaibot.com/api/ask"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Tenant ID</label>
            <input value={cfg.tenantId} onChange={(e) => setCfg({ ...cfg, tenantId: e.target.value })} placeholder="x1493"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Tenant API Key（Bearer）</label>
            <p className="mt-1 text-sm">
              {cfg.apiKeySet
                ? <span className="font-bold text-green-600 dark:text-green-400">✓ 已配置（Pages secret AI_ASSISTANT_API_KEY）</span>
                : <span className="font-bold text-red-500">✗ 未配置——npx wrangler pages secret put AI_ASSISTANT_API_KEY --name x1493-shop</span>}
            </p>
          </div>
          <button onClick={testConn} disabled={testing} className="w-fit rounded-lg bg-gray-100 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200">
            {testing ? '測試中…' : '🔌 測試連線'}
          </button>
          {testResult && (
            <p className={`text-sm font-medium ${testResult.ok ? 'text-green-600' : 'text-red-500'}`}>{testResult.text}</p>
          )}
        </div>
      </div>

      {/* 總掣卡 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">⚡ 總掣（雙重鎖之一）</h3>
        <label className="mt-3 flex cursor-pointer items-center gap-3">
          <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} className="h-5 w-5" />
          <span className="text-sm font-bold text-gray-800 dark:text-white">AI 助手啟用</span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${cfg.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
            {cfg.enabled ? '● ON' : '○ OFF'}
          </span>
        </label>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          開啟後：關鍵字引擎冇命中嘅一般查詢 → AIBOT /ask 自動回覆（origin='ai'）。
          客人發「轉人工／搵真人」→ 即時還人＋admin 群組通知。
        </p>
      </div>

      {/* 規則卡 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">📏 規則</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">對話冷卻（秒）</label>
            <input type="number" value={cfg.cooldownSec} onChange={(e) => setCfg({ ...cfg, cooldownSec: Number(e.target.value) })}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">活躍時段（HKT，空＝24/7）</label>
            <input value={cfg.activeHours} onChange={(e) => setCfg({ ...cfg, activeHours: e.target.value })} placeholder="09:00-23:00"
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-white" />
          </div>
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">轉人工關鍵字（固定）：轉人工／搵真人／搵同事／human agent——命中即還人＋admin 通知。</p>
      </div>

      {/* 通知卡（AI 掌櫃反饋目標） */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">📣 通知目標（AI 掌櫃反饋）</h3>
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
          {cfg.notifyTarget
            ? <>辣谷管理員群組<span className="ml-2 text-xs text-gray-400">{cfg.notifyTarget}</span></>
            : <span className="text-amber-600 dark:text-amber-400">未設定群組——目前 fallback 逐個 admin 號碼發送</span>}
        </p>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          分類警報（投訴／售後）與 AI 轉人手提醒會發到這裡。改群組：在辣谷管理群組內發「群組 on」即自動綁定。
        </p>
        <button onClick={testNotify} disabled={notifyTesting} className="mt-3 w-fit rounded-lg bg-gray-100 px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-700 dark:text-gray-200">
          {notifyTesting ? '發送中…' : '📨 發送測試通知'}
        </button>
        {notifyResult && (
          <p className={`mt-2 text-sm font-medium ${notifyResult.ok ? 'text-green-600' : 'text-red-500'}`}>{notifyResult.text}</p>
        )}
      </div>

      {/* 分類卡（AI 掌櫃・自動分類） */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">🗂 自動分類（第一層・關鍵字）</h3>
        <label className="mt-3 flex cursor-pointer items-center gap-3">
          <input type="checkbox" checked={cfg.autotagEnabled} onChange={(e) => setCfg({ ...cfg, autotagEnabled: e.target.checked })} className="h-5 w-5" />
          <span className="text-sm font-bold text-gray-800 dark:text-white">入站訊息自動分類（只填未分類對話，人工標註不被覆蓋）</span>
        </label>
        <div className="mt-3 space-y-2">
          {Object.entries(keywords).map(([cat, kws]) => (
            <div key={cat} className="flex flex-wrap items-start gap-1.5">
              <span className={`shrink-0 rounded px-2 py-0.5 text-[11px] font-bold ${cat === '投訴' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : cat === '售後' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                {cat}{(cat === '投訴' || cat === '售後') && ' → 群組警報'}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{kws.join('・')}</span>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">關鍵字表為固定第一層；第二層 AI 分類待 AIBOT 租戶開通後啟用。</p>
      </div>

      {/* 最近 AI 互動 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">🤖 最近 AI 回覆（{recent.length}）</h3>
        {recent.length === 0 ? (
          <p className="mt-2 text-xs text-gray-400">暫無——AI 掌櫃開閘後這裡會列出自動回覆記錄。</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {recent.slice(0, 10).map((r) => (
              <div key={r.id} className="flex items-start justify-between gap-2 border-b border-gray-100 py-1.5 text-xs last:border-0 dark:border-gray-700">
                <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{r.phone}｜{r.preview}</span>
                <span className="shrink-0 text-gray-400">{r.created_at ? new Date(r.created_at * 1000).toLocaleString('zh-HK', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 👍👎 反饋 */}
      <div className="rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="text-base font-bold text-gray-800 dark:text-white">👍👎 反饋（{feedback.length}）</h3>
        {feedback.length === 0 ? (
          <p className="mt-2 text-xs text-gray-400">暫無——反饋迴路（對 AI 回覆評分）待後續啟用。</p>
        ) : (
          <div className="mt-2 space-y-1.5">
            {feedback.slice(0, 10).map((f) => (
              <div key={f.id} className="flex items-start justify-between gap-2 border-b border-gray-100 py-1.5 text-xs last:border-0 dark:border-gray-700">
                <span className="min-w-0 truncate text-gray-600 dark:text-gray-300">{f.feedback === 'up' ? '👍' : '👎'} {f.phone}{f.comment ? `｜${f.comment}` : ''}</span>
                <span className="shrink-0 text-gray-400">{f.created_at ? new Date(f.created_at * 1000).toLocaleString('zh-HK', { month: 'numeric', day: 'numeric' }) : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-50">
        <Save size={15} /> {saving ? '儲存中…' : '儲存設置'}
      </button>
    </div>
  );
};

const AdminSettings: React.FC = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState<Record<string, SettingItem>>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [currentRole, setCurrentRole] = useState<string>('');
  const [dangerAction, setDangerAction] = useState<DangerActionDef | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [dangerLoading, setDangerLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'whatsapp' | 'ai'>('general');

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    const storedUser = localStorage.getItem('admin_user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setCurrentRole(user?.role || '');
      } catch {
        setCurrentRole('');
      }
    }
    fetchSettings();
  }, [token, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        const data = json.data ?? json;
        const merged = { ...defaultSettings };
        for (const [key, item] of Object.entries(data)) {
          if (key.startsWith('whatsapp_')) continue;
          if (merged[key]) {
            merged[key] = { ...merged[key], value: (item as any).value || item };
          } else {
            merged[key] = { value: (item as any).value || item, description: (item as any).description || '' };
          }
        }
        delete (merged as any).min_days_advance;
        setSettings(merged);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      }
    } catch (e) {
      setError('獲取設置失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload: Record<string, any> = {};
      for (const [key, item] of Object.entries(settings)) {
        payload[key] = { value: item.value, description: item.description };
      }

      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSuccess('設置已保存！');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('保存失敗');
      }
    } catch (e) {
      setError('保存失敗');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const canUseDangerZone = currentRole === 'super_admin' || currentRole === 'admin';

  const openDangerAction = (action: DangerActionDef) => {
    if (!canUseDangerZone) return;
    setDangerAction(action);
    setConfirmText('');
    setError('');
  };

  const closeDangerAction = () => {
    setDangerAction(null);
    setConfirmText('');
  };

  const executeDangerAction = async () => {
    if (!dangerAction) return;
    if (confirmText !== dangerAction.confirmPhrase) {
      setError(`確認碼不正確，請輸入「${dangerAction.confirmPhrase}」`);
      return;
    }
    setDangerLoading(true);
    setError('');
    try {
      const res = await apiFetch(dangerAction.endpoint, { method: 'DELETE' });
      if (res.ok) {
        setSuccess(`已執行：${dangerAction.label}`);
        closeDangerAction();
        setTimeout(() => setSuccess(''), 4000);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || `執行 ${dangerAction.label} 失敗`);
      }
    } catch (e) {
      setError('網絡錯誤，操作未成功');
    } finally {
      setDangerLoading(false);
    }
  };

  return (
    <AdminLayout currentPage="settings-basic">
      <div>
        {/* Tab bar */}
        <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('general')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${activeTab === 'general' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <SettingsIcon size={16} /> 系統設置
          </button>
          <button
            onClick={() => setActiveTab('whatsapp')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${activeTab === 'whatsapp' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <MessageCircle size={16} /> WhatsApp 連線
          </button>
          <button
            onClick={() => setActiveTab('ai')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition ${activeTab === 'ai' ? 'border-red-600 text-red-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
          >
            <Bot size={16} /> AI 助手
          </button>
        </div>

        {activeTab === 'whatsapp' ? (
          <AdminWhatsAppTab />
        ) : activeTab === 'ai' ? (
          <AiAssistantTab />
        ) : (
        <>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center gap-2">
            <AlertCircle size={20} />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {loading ? (
          <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">加載中...</div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">系統設置</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(settings).map(([key, item]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {item.description}
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-mono">({key})</span>
                  </label>
                  <input
                    value={item.value}
                    onChange={e => updateSetting(key, e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none dark:bg-gray-800 dark:text-white"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 font-medium"
              >
                <Save className="w-5 h-5 mr-2" />
                {saving ? '保存中...' : '保存設置'}
              </button>
            </div>
          </div>
        )}

        {/* Danger zone */}
        <div className="mt-8 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            <h3 className="text-lg font-bold text-red-800 dark:text-red-200">危險刪除區</h3>
          </div>
          <p className="text-sm text-red-700 dark:text-red-300 mb-4">
            以下操作會永久刪除資料，無法復原。僅供管理員使用。
          </p>

          {!canUseDangerZone && (
            <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-sm rounded-lg">
              你目前的權限無法使用危險刪除功能。
            </div>
          )}

          <div className="space-y-3">
            {DANGER_ACTIONS.map((action) => (
              <div
                key={action.key}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white dark:bg-gray-800 border border-red-200 dark:border-red-800 rounded-lg p-4"
              >
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">{action.label}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{action.description}</div>
                </div>
                <button
                  onClick={() => openDangerAction(action)}
                  disabled={!canUseDangerZone}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  <Trash2 size={16} /> 刪除
                </button>
              </div>
            ))}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Danger confirmation modal */}
      {dangerAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertTriangle size={22} />
                <h3 className="text-lg font-bold">確認{dangerAction.label}</h3>
              </div>
              <button onClick={closeDangerAction} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={22} />
              </button>
            </div>

            <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
              你即將{dangerAction.label}。此操作不可復原，請在下方輸入確認碼：
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3 mb-4 text-center font-mono text-gray-900 dark:text-white select-all">
              {dangerAction.confirmPhrase}
            </div>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={`輸入「${dangerAction.confirmPhrase}」以確認`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none dark:bg-gray-800 dark:text-white mb-6"
            />

            <div className="flex justify-end gap-3">
              <button
                onClick={closeDangerAction}
                disabled={dangerLoading}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white"
              >
                取消
              </button>
              <button
                onClick={executeDangerAction}
                disabled={dangerLoading || confirmText !== dangerAction.confirmPhrase}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
              >
                {dangerLoading ? '執行中...' : <><Trash2 size={16} /> 確認刪除</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSettings;
